import { useState, useEffect } from 'react'
import mqtt from 'mqtt'
import WebService from '@util/webService'
import { useSelector } from 'react-redux'

/**
 * 브로커 접속 + 구독 + 수신 상태를 한 곳에 모은 훅.
 *
 * subscriptions: [{ topic, service, idField }, ...]
 *   topic   -- 서버 응답(topicNames/records)의 키. 어느 토픽을 구독할지 서버에 알리는 값이기도 하다.
 *   service -- 봉투(RemoteMessage.service)의 판별값. topic에서 유도하지 않는다 --
 *              발행자마다 이름 규약이 달라서(수집기는 "binance.collector") 유도하면 언젠가 어긋난다.
 *   idField -- content 안에서 맵 키로 쓸 필드명. **없으면 레코드를 안 쌓는다**(장애만 보겠다는 선언).
 *
 * 반환
 *   records   -- content에서 나온 데이터. { wallet: { 37: {...} }, signal: { 1: {...} } }
 *   services  -- 봉투에서 나온 소식. { 'trader.wallet': { error, exchangeId, at } }
 *   connected -- mqtt 연결 여부. 화면이 "끊김"과 "데이터 없음"을 구분해야 한다.
 *
 * 접속은 **하나**다. 토픽이 여러 개여도 구독만 여러 개 건다.
 *
 * 사용:
 *   const { records, services, connected } = useBroker([
 *     { topic: 'wallet',    service: 'trader.wallet', idField: 'walletId' },
 *     { topic: 'collector', service: 'binance.collector' },
 *   ])
 *   const wallets = Object.values(records.wallet)
 */
const useBroker = (subscriptions, userId = '') => {
  const account = useSelector(state => state.accountReducer)
  const targetUserId = userId === '' ? account.user.id : userId
  const [broker, set_broker] = useState(null)
  const [connected, set_connected] = useState(false)
  const [services, set_services] = useState({})

  // idField가 있는 구독만 자리를 만든다 -- 상태만 보는 구독엔 레코드 개념이 없다.
  // 미리 깔아두는 이유: 페이지가 fetch 전에 records.wallet을 만져도 undefined가 아니라 {}가 나온다.
  const [records, set_records] = useState(() => {
    const empty = {}
    subscriptions.forEach(s => { if (s.idField) empty[s.topic] = {} })
    return empty
  })

  // ⚠ subscriptions는 호출부에서 인라인 배열로 넘어오기 쉬워 **매 렌더 새 참조**가 된다.
  // 그대로 useEffect deps에 넣으면 접속을 무한히 다시 맺는다. 내용이 같으면 같은 문자열이
  // 되는 키를 만들어 그걸 deps로 쓴다.
  //
  // 중복을 거르는 이유: 한 토픽에 여러 service가 오는 게 정상이다(dashboard 토픽 하나로
  // binance.collector / bitget.collector / …가 전부 온다). 안 거르면 서버에
  // "topics=dashboard,dashboard,dashboard,dashboard"를 보내게 된다.
  const topicKeys = [...new Set(subscriptions.map(s => s.topic))].join(',')

  // service -> 이 구독. 봉투의 service로 바로 찾는다. 표에 없는 service는 자동으로 무시된다.
  const subOfService = {}
  subscriptions.forEach(s => { subOfService[s.service] = s })

  const fetchBroker = async () => {
    // 필요한 토픽만 요청한다 -- 서버가 요청한 것만 내려주고, 권한이 없으면 요청 자체가 거부된다.
    const url = `/broker?topics=${topicKeys}&targetUserId=${targetUserId}`
    const resp = await WebService.get(url)
    if (resp.repCode !== 'ack') {
      console.error('broker 조회 실패=>', resp.repMessage)
      return
    }

    const repMessage = JSON.parse(resp.repMessage)
    const brokerInfo = JSON.parse(repMessage.broker)
    const recordsByTopic = JSON.parse(repMessage.records || '{}')

    // 서버가 준 배열([A, B])을 idField로 찾을 수 있는 객체({37: A, 38: B})로 바꾼다.
    // mqtt 갱신이 같은 키로 자리를 찾아 덮어써야 하므로 배열이 아니라 객체로 든다.
    const initial = {}
    subscriptions.forEach(s => {
      if (!s.idField) {
        return
      }
      const list = recordsByTopic[s.topic] || []
      const byId = {}
      for (let i = 0; i < list.length; i++) {
        const record = list[i]
        byId[record[s.idField]] = record
      }
      initial[s.topic] = byId
    })

    set_records(initial)
    set_broker(brokerInfo)
  }

  useEffect(() => {
    fetchBroker()
  }, [topicKeys])

  useEffect(() => {
    if (!broker || !broker.brokerHost) {
      return
    }

    // [2026-08-11] 스킴을 **페이지에서** 유도한다. HTTPS로 연 페이지에서 `ws://`를 열면
    // 브라우저가 혼합 콘텐츠로 막는다(SecurityError, 사용자가 우회할 방법이 없다). deck에
    // 인증서를 붙인 날 드러났고, 그전까지 http로 열고 있어서 안 보였을 뿐이다.
    //
    // 운영에서 brokerHost/brokerMqttPort를 **안 쓰는** 이유: 인증서에 적힌 이름은
    // deck.jinvis.com 이라, 브로커 호스트명이나 IP로 wss를 열면 이름이 안 맞아 검증에서 죽는다.
    // 아파치가 443에서 /ws/를 브로커로 프록시하므로(httpd-vhosts.conf의 proxy_wstunnel 규칙)
    // same-origin으로 붙으면 그 문제가 통째로 없어지고, 브로커 포트를 밖에 열 필요도 없다.
    //
    // 개발(http)은 그 프록시가 없으니 브로커 직결을 그대로 쓴다 -- 두 갈래인 이유가 그것이다.
    const secure = window.location.protocol === 'https:'
    const brokerUrl = secure
      ? `wss://${window.location.host}/ws/`
      : `ws://${broker.brokerHost}:${broker.brokerMqttPort}/ws/`

    const client = mqtt.connect(brokerUrl, {
      username: broker.brokerLoginId,
      password: broker.brokerLoginPassword,
      clientId: `${targetUserId}-${crypto.randomUUID().slice(0, 8)}`,
      clean: true,
      connectTimeout: 3000,
      reconnectPeriod: 0,
    })

    client.on('connect', () => {
      set_connected(true)
      // 서버가 내려준 것만 구독한다 -- 권한이 없는 토픽은 키 자체가 없다.
      Object.values(broker.topicNames).forEach(topicName => {
        client.subscribe(topicName, { qos: 0 })
      })
    })

    client.on('close', () => set_connected(false))
    client.on('error', (e) => {
      console.error('mqtt error=>', e)
      set_connected(false)
    })

    client.on('message', (topic, payload) => {
      try {
        const msg = JSON.parse(payload.toString())

        // 브로커로 오는 모든 메시지는 RemoteMessage 봉투다(수집기·트레이더 공통).
        // **topic 문자열이 아니라 service로 가른다** -- 한 토픽에 여러 종류가 올 수 있고,
        // 페이지가 토픽을 몇 개 구독하든 이 분기가 안 늘어난다.
        const sub = subOfService[msg.service]
        if (!sub) {
          return
        }

        // [봉투] error는 content와 **무관하게 항상** 기록한다. 수집기처럼 content를 안 싣고
        // 장애만 알리는 발행자가 있기 때문이다. error가 ''이면 정상이라는 뜻이고, at이
        // 갱신되므로 "장애"와 "발행 자체가 끊김"을 화면이 구분할 수 있다.
        set_services(prev => {
          const next = Object.assign({}, prev)
          next[msg.service] = {
            error: msg.error || '',
            exchangeId: msg.exchangeId,
            at: Date.now(),
          }
          return next
        })

        // [내용] content가 없으면 여기서 끝이다 -- 상태만 보내는 메시지다.
        if (!msg.content) {
          return
        }
        if (!sub.idField) {
          return
        }

        // content가 String 필드라 JSON 문자열이 JSON 안에 들어있다 -- 한 번 더 판다.
        const data = JSON.parse(msg.content)

        // exchangeId는 봉투에만 있고 content 안에는 없다 -- 여기서 붙여준다.
        data.exchangeId = msg.exchangeId

        const id = data[sub.idField]

        set_records(prev => {
          // 기존 것을 복사한 **새 객체**를 만든다. 제자리에서 고치면(prev[topic][id] = x)
          // React가 참조가 그대로라 안 바뀐 걸로 보고 화면을 다시 그리지 않는다.
          const next = Object.assign({}, prev)
          const byId = Object.assign({}, next[sub.topic])

          // 스냅샷이라 통째로 바꿔 끼운다. 필드 단위로 합치면 사라진 값(청산된 position,
          // 취소된 orders)이 화면에 계속 남는다.
          byId[id] = data
          next[sub.topic] = byId

          return next
        })
      }
      // 메시지 하나가 깨졌다고 구독이 죽으면 안 된다(서버 doProcess와 같은 방침).
      catch (e) {
        console.error('mqtt 메시지 처리 실패 -- 이 건은 버린다', e)
      }
    })

    return () => {
      client.end()
      set_connected(false)
    }
  }, [broker])

  return { records, services, connected }
}

export default useBroker
